import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Gift, Camera, Palette, Clock, Star, Award } from 'lucide-react';

const AIImageGeneratorNOTHS = () => {
  const faqs = [
    {
      question: "What image requirements does notonthehighstreet have?",
      answer: "NOTHS requires high-quality images with clean backgrounds, typically at least 1500x1500 pixels. Our AI generator creates professional images that meet these standards, perfect for the NOTHS marketplace aesthetic."
    },
    {
      question: "Can I create lifestyle images for my NOTHS products?",
      answer: "Absolutely! Our AI excels at generating beautiful lifestyle photography, styled flat lays, and gift-ready imagery that resonates with NOTHS customers looking for unique, personalised products."
    },
    {
      question: "Are AI-generated images suitable for notonthehighstreet?",
      answer: "Yes, AI-generated images work perfectly for product photography on NOTHS. Our platform provides full commercial rights, and images are designed to match the premium, artisan aesthetic of the marketplace."
    },
    {
      question: "How can I showcase personalised products with AI?",
      answer: "Generate beautiful product mockups showing personalisation options. Create images demonstrating different names, dates, or custom text on your products without photographing each variation."
    }
  ];

  const features = [
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Gift-Worthy Images",
      description: "Create beautiful imagery that showcases products as perfect gifts for every occasion"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Artisan Aesthetic",
      description: "Generate images matching NOTHS's premium, handcrafted marketplace style"
    },
    {
      icon: <Image className="w-6 h-6" />,
      title: "Lifestyle Shots",
      description: "Create in-situ imagery showing products in beautiful home settings"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Personalisation Previews",
      description: "Showcase personalisation options without photographing every variation"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Seasonal Themes",
      description: "Generate Christmas, wedding, birthday, and occasion-specific imagery"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Premium Quality",
      description: "High-resolution images that reflect the quality of your handmade products"
    }
  ];

  const useCases = [
    "Personalised gift sellers on notonthehighstreet",
    "Handmade jewellery and accessory makers",
    "Home décor and homeware artisans",
    "Wedding and occasion stationery designers",
    "Children's gifts and nursery décor sellers",
    "Personalised home and kitchen product makers",
    "Artisan food and drink gift sellers",
    "Custom artwork and print sellers"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for notonthehighstreet | NOTHS Product Photos"
        description="Create stunning notonthehighstreet product photos with AI. Gift-worthy imagery, lifestyle shots, personalisation previews for NOTHS sellers. Used by 2,000+ UK artisans."
        keywords="ai image generator notonthehighstreet, noths product photos, notonthehighstreet listing images, noths seller photography, notonthehighstreet images, noths product photography, noths seller tools uk"
        url="/ai-image-generator-notonthehighstreet"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for NOTHS', url: '/ai-image-generator-notonthehighstreet' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-cyan-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 text-foreground border-0">
                Trusted by 2,000+ UK NOTHS Sellers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for NOTHS
                <span className="block bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-sm">
                  notonthehighstreet Product Photos
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create beautiful notonthehighstreet product images with AI. Gift-worthy photography, 
                lifestyle shots, and personalisation previews that capture the artisan spirit of NOTHS.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create NOTHS Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Artisan Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from UK artisans</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>NOTHS Aesthetic</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-500" />
                  <span>Premium Quality</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why UK NOTHS Sellers Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-teal-500/5 to-cyan-500/5 hover:from-teal-500/10 hover:to-cyan-500/10 hover:border-teal-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every NOTHS Seller
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-teal-500/5 to-cyan-500/5">
                    <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              Selling on Other UK Platforms?
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-for-etsy">AI for Etsy UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-amazon-uk">AI for Amazon UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-depop">AI for Depop</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-teal-500/5 to-cyan-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Beautiful NOTHS Listings Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 2,000+ UK artisans using AI to create gift-worthy product photos. 
                No photography skills or expensive equipment needed.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate NOTHS Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorNOTHS;
